import { QueryLoading } from 'modules/common/components/QueryLoading';
import { PropsWithChildren, useEffect } from 'react';
import { useVaultBalance } from '../../hooks/useVaultBalance';
import { ZERO } from 'modules/common/const';
import { useNavigate } from 'react-router-dom';
import { dashboardRouteConfig } from 'modules/dashboard';
import { useIsWithdrawalInProgress } from '../../hooks/useIsWithdrawalInProgress';
import { useGlobalTranslation } from 'modules/i18n';
import { useUserUnlockTime } from '../../hooks/useUserUnlockTime';
import { useNotificationAndRedirect } from '../../hooks/useNotificationAndRedirect';
import { useBoringVaultV1 } from 'boring-vault-ui';

interface IWithdrawalGuardProps extends PropsWithChildren {}

const mainPage = dashboardRouteConfig.main.generatePath();

const formatDate = (timestamp: number) => {
  return new Date(timestamp).toLocaleString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export function WithdrawalGuard({
  children,
}: IWithdrawalGuardProps): JSX.Element {
  const navigate = useNavigate();
  const { t, keys } = useGlobalTranslation();

  const { balance, isLoading } = useVaultBalance();
  const { isWithdrawalInProgress, isLoading: isWithdrawalLoading } =
    useIsWithdrawalInProgress();

  const { data: unlockTime = 0, isLoading: isUserUnlockTimeLoading } =
    useUserUnlockTime();
  const { isBoringV1ContextReady } = useBoringVaultV1();

  const notify = useNotificationAndRedirect();

  useEffect(() => {
    if (
      !balance.isGreaterThan(ZERO) &&
      !isLoading &&
      !isWithdrawalLoading &&
      !isWithdrawalInProgress
    ) {
      notify(t(keys.withdrawal), mainPage);
    }
  }, [
    isLoading,
    balance,
    isWithdrawalInProgress,
    isWithdrawalLoading,
    notify,
    t,
    keys,
  ]);

  useEffect(() => {
    if (!isBoringV1ContextReady) {
      return;
    }

    const time = unlockTime * 1000;
    const unlockTimeLeft = time - Date.now();

    if (
      unlockTimeLeft > 0 &&
      !isUserUnlockTimeLoading &&
      !isWithdrawalLoading &&
      !isWithdrawalInProgress
    ) {
      notify(
        t(keys.fundsLocked, {
          time: formatDate(time),
        }),
        mainPage,
      );
    }
  }, [
    navigate,
    unlockTime,
    isUserUnlockTimeLoading,
    t,
    keys,
    notify,
    isBoringV1ContextReady,
    isWithdrawalLoading,
    isWithdrawalInProgress,
  ]);

  if (balance.isGreaterThan(ZERO) || isWithdrawalInProgress) {
    return <>{children}</>;
  }

  return <QueryLoading isAbsolute />;
}
